// Institute module interfaces — lifecycle matches OpenAPI `GetInstituteResponse.status`

export const INSTITUTE_STATUSES = [
  "archived",
  "pending",
  "active",
  "draft",
  "other",
] as const;

export type TInstituteStatus = (typeof INSTITUTE_STATUSES)[number];

/** Status values allowed when creating an institute (archiving remains a dedicated action). */
export const INSTITUTE_CREATE_STATUSES = [
  "draft",
  "pending",
  "active",
  "other",
] as const satisfies readonly TInstituteStatus[];

export type TInstituteCreateStatus = (typeof INSTITUTE_CREATE_STATUSES)[number];

export type TInstituteType =
  | "CBSE"
  | "ICSE"
  | "State Board"
  | "IB"
  | "IGCSE"
  | "Other";

export type TAddressType = "correspondence" | "registered" | "other";

export type TAddress = {
  id: string;
  instituteId: string;
  type: TAddressType; // just another string like "correspondence", "registered", "other"
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isPrimary: boolean;
  createdAt?: string;
  updatedAt?: string;
};

export type TInstitute = {
  id: string;
  name: string;
  type: TInstituteType;
  registrationNumber: string;
  registrationDate?: string;
  legalEntity?: string;
  status: TInstituteStatus;
  configurationCompleteness: number; // Percentage 0-100
  contactInformation?: {
    primaryPhone?: string;
    secondaryPhone?: string;
    /** Wire may nest email as `{ email, is_verified }`; maps to a plain string */
    email?: string;
    emailVerified?: boolean;
    website?: string;
    fax?: string;
  };
  addresses?: TAddress[];
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  deletedBy?: {
    created_at: string;
    first_name: string;
    last_name: string;
    user_name: string;
    user_role: string;
    _id: string;
  };
};

export type TCreateInstituteRequest = {
  name: string;
  type: TInstituteType;
  registrationNumber: string;
  registrationDate: string;
  legalEntity: string;
  /** Initial lifecycle status; applied via follow-up PATCH when POST omits `status`. */
  status: TInstituteCreateStatus;
};

/** PATCH `/v1/institutes/by-id/{id}` — all fields optional except callers typically send a full form payload. */
export type TUpdateInstituteRequest = {
  name?: string;
  type?: TInstituteType;
  registrationNumber?: string;
  registrationDate?: string;
  legalEntity?: string;
  status?: TInstituteStatus;
  contactInformation?: {
    primaryPhone?: string;
    secondaryPhone?: string;
    email?: string;
    website?: string;
    fax?: string;
  };
};

export type TCreateAddressRequest = {
  instituteId: string;
  type: TAddressType;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isPrimary: boolean;
};

export type TUpdateAddressRequest = {
  type?: TAddressType;
  streetAddress?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  isPrimary?: boolean;
};

// ---------------------------------------------------------------------------
// Onboard — atomic institute + admin creation
// ---------------------------------------------------------------------------

export type TOnboardInstituteAdminRequest = {
  firstName: string;
  middleName?: string;
  lastName: string;
  gender: "male" | "female" | "trans";
  userName: string;
  userPassword: string;
  designation: string;
  email: string;
  countryCode: string;
  phoneNumber: string;
  city: string;
  state: string;
  country: string;
  pinCode: string;
};

export type TOnboardInstituteRequest = {
  name: string;
  type: TInstituteType;
  registrationNumber: string;
  registrationDate: string;
  legalEntity: string;
  status: TInstituteCreateStatus;
  domainSlug: string;
  admin: TOnboardInstituteAdminRequest;
};
